import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../../config/api';
import './Avatar.css';

const SIZE_CLASS = {
  xs: 'avatar--xs',
  sm: 'avatar--sm',
  md: 'avatar--md',
  lg: 'avatar--lg',
  xl: 'avatar--xl'
};

const PALETTE = [
  '#4F46E5',
  '#0EA5E9',
  '#14B8A6',
  '#84CC16',
  '#F59E0B',
  '#F43F5E',
  '#A855F7',
  '#64748B'
];

function hashCode(value) {
  const text = String(value || '');
  let hash = 0;
  for (let index = 0; index < text.length; index += 1) {
    hash = ((hash << 5) - hash) + text.charCodeAt(index);
    hash |= 0;
  }
  return Math.abs(hash);
}

function getInitials(name, email) {
  const sourceName = String(name || '').trim();
  if (sourceName) {
    const parts = sourceName.split(/\s+/).filter(Boolean);
    if (parts.length === 1) {
      return parts[0][0]?.toUpperCase() || null;
    }
    return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase() || null;
  }

  const localPart = String(email || '').split('@')[0]?.trim();
  return localPart ? localPart[0]?.toUpperCase() || null : null;
}

function getAvatarImageSrc({ avatarUrl, imageUrl, userId, version }) {
  if (imageUrl) return imageUrl;
  if (!avatarUrl || !userId) return '';
  const cacheKey = version || avatarUrl;
  return `${API_BASE_URL}/api/users/${encodeURIComponent(userId)}/avatar?v=${encodeURIComponent(cacheKey)}`;
}

function Avatar({
  avatarUrl = '',
  className = '',
  email = '',
  identifier = '',
  imageUrl = '',
  name = '',
  size = 'md',
  standalone = false,
  userId = null,
  version = ''
}) {
  const [imageFailed, setImageFailed] = useState(false);
  const resolvedSrc = getAvatarImageSrc({ avatarUrl, imageUrl, userId, version });
  const initials = getInitials(name, email);
  const stableIdentifier = identifier || userId || email || name || 'unknown';
  const background = useMemo(
    () => PALETTE[hashCode(stableIdentifier) % PALETTE.length],
    [stableIdentifier]
  );

  useEffect(() => {
    setImageFailed(false);
  }, [resolvedSrc]);

  const classes = [
    'avatar',
    SIZE_CLASS[size] || SIZE_CLASS.md,
    resolvedSrc && !imageFailed ? 'avatar--photo' : '',
    !initials ? 'avatar--icon' : '',
    className
  ].filter(Boolean).join(' ');

  const label = name || email || 'Unknown user';

  return (
    <span
      className={classes}
      style={{ '--avatar-bg': background }}
      title={label}
      aria-label={standalone ? label : undefined}
      aria-hidden={standalone ? undefined : 'true'}
    >
      {resolvedSrc && !imageFailed ? (
        <img
          alt=""
          src={resolvedSrc}
          srcSet={`${resolvedSrc} 1x, ${resolvedSrc} 2x`}
          onError={() => setImageFailed(true)}
          loading="lazy"
        />
      ) : initials ? (
        <span className="avatar__initials">{initials}</span>
      ) : (
        <svg className="avatar__icon" viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4Zm0 2c-3.31 0-6 1.67-6 3.73V20h12v-2.27C18 15.67 15.31 14 12 14Z"
            fill="currentColor"
          />
        </svg>
      )}
    </span>
  );
}

export default Avatar;
